import React, { useRef, useState } from "react"
import { Box, FormControlLabel, Link, Typography } from "@mui/material"
import FullscreenModal from "../../../generic-shared-components/components/FullscreenModal"
import Button from "../../../generic-shared-components/components/Button"
import Checkbox from "../../../generic-shared-components/components/Checkbox"
import { useDocumentContext } from "../DocumentModal"
import { LegalDocument } from "../../../../../../../../packages/mp-shared-components/src/legal/LegalDocument"
import { LegalContent } from "../../../../../../../../packages/mp-shared-components/src/legal/documents"
import { useStyles } from "./styles"

interface Props {
  agreementContent: LegalContent | null
  onAgree?: () => Promise<boolean>
  onLogout: () => void
  onRetry?: () => void
  error?: string
  saveLoading?: boolean
  checkLoading?: boolean
}

const TermsOfServiceModal: React.FC<Props> = ({
  agreementContent, onAgree, onLogout, onRetry, error = "", saveLoading = false, checkLoading = false
}) => {
  const { classes } = useStyles()
  const [isChecked, setIsChecked] = useState(false)
  const saving = useRef(false)
  const content = useRef<HTMLDivElement>(null)
  const { showDocument, setDocumentAndVisibility } = useDocumentContext()

  const handleContinue = async () => {
    if (!isChecked || saveLoading || saving.current || !onAgree) return
    saving.current = true
    try { await onAgree() } finally { saving.current = false }
  }

  return (
    <FullscreenModal
      open
      inactive={showDocument}
      title={agreementContent?.title || "MasteryPrep Terms of Use"}
      whiteBackground
      dataAutomationId="acceptance-agreement"
      onEntered={() => content.current?.focus()}
      outerClasses={{ paper: classes.zoomScrollPaper, content: classes.zoomScrollContent, footer: classes.footer }}
      actions={
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={4} width="100%">
          <Button size="md" onClick={onLogout} data-automation-id="acceptance-agreement-logout">Logout</Button>
          {agreementContent && (
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={event => { if (!saveLoading) setIsChecked(event.target.checked) }}
                    inputProps={{ "aria-disabled": saveLoading }}
                    data-automation-id="acceptance-agreement-checkbox"
                  />
                }
                label={
                  <Typography component="span" variant="body1" data-automation-id="acceptance-agreement-checkbox-text">
                    {agreementContent.metadata?.checkboxText || "I agree to the MasteryPrep Terms of Use"}
                  </Typography>
                }
              />
              <Button
                size="md"
                variant="contained"
                color="secondary"
                isDisabled={!isChecked || saveLoading}
                loading={saveLoading}
                onClick={handleContinue}
                data-automation-id="acceptance-agreement-continue"
              >Continue</Button>
            </Box>
          )}
        </Box>
      }
    >
      <Box role="status" aria-live="polite" data-automation-id="acceptance-agreement-status">
        {checkLoading ? "Loading the current Terms of Use…" : saveLoading ? "Saving your acceptance…" : ""}
      </Box>
      <Box role="alert" data-automation-id="acceptance-agreement-error">{error}</Box>
      {!checkLoading && !agreementContent && onRetry && (
        <Box marginTop={4}>
          <Button size="md" onClick={onRetry} data-automation-id="acceptance-agreement-retry-button">Retry</Button>
        </Box>
      )}
      <Box ref={content} tabIndex={-1} aria-busy={checkLoading || saveLoading}>
        {agreementContent && (
          <>
            <Typography component="p" variant="body1" sx={{ marginBottom: 4 }}>
              {agreementContent.metadata?.instructions || "Please read and agree to the updated Terms of Use."}
            </Typography>
            <Link href="#privacy-policy" onClick={event => {
              event.preventDefault()
              setDocumentAndVisibility("privacy-policy", true)
            }} data-automation-id="acceptance-agreement-privacy-policy-link">Privacy Policy</Link>
            <LegalDocument content={agreementContent} onOpenDocument={type => setDocumentAndVisibility(type, true)} />
          </>
        )}
      </Box>
    </FullscreenModal>
  )
}

export default TermsOfServiceModal
